/**
 * Admin API payload → domain model mapping.
 *
 * Keeps the nullable, PascalCase-ish API surface out of the admin UI: screens
 * receive fully populated models with empty strings instead of nulls.
 */
import { IS_DEV } from '../../config/env';
import { getFullImageUrl, resolveProfilePictureUrl } from '../../utils/imageUrl';
import {
  hasModerationParentTarget,
  isModerationMediaTarget,
  readModerationCategory,
  readModerationSeverity,
  readModerationStage,
  readModerationStatus,
  readModerationSurface,
  readModerationTargetType,
} from '../moderation';
import type {
  AdminLoginEvent,
  AdminPage,
  AdminPagedResult,
  AdminUser,
  AdminUserDetailsResponse,
  AdminUserRole,
  ApiReportedContentComment,
  ApiReportedContentData,
  ApiReportedContentItem,
  DashboardMetric,
  DashboardNew,
  LoginInfoItem,
  ModerationBacklogCount,
  ModerationQueueItem,
  ModerationQueuePage,
  ModerationReviewOutcome,
  ModerationSetting,
  ModerationSettingsOverview,
  ModerationSignal,
  ModerationUsage,
  ModerationUsageBreakdown,
  ReportedContentItem,
  ReportedContentPage,
} from './adminTypes';

const text = (value: string | null | undefined): string => value ?? '';

const count = (value: number | null | undefined): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

/** Accepts the string-encoded integers some admin endpoints emit. */
const numeric = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const optionalText = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

/**
 * Property lookup keyed by lower-cased name. The admin controller is not
 * consistent about casing (`userGUID`, `reportedPostID`), so dashboard fields
 * are read case-insensitively rather than trusting one spelling.
 */
const indexByLowerKey = (source: Record<string, unknown>): Map<string, unknown> => {
  const index = new Map<string, unknown>();
  for (const [key, value] of Object.entries(source)) {
    index.set(key.toLowerCase(), value);
  }
  return index;
};

/**
 * Distinctive dashboard counter names. Any one identifies the body, so a
 * response omitting some counters still resolves.
 *
 * `metrics` is deliberately NOT a marker: it is an array the API could also
 * hang off an outer envelope, and matching on it alone would resolve to that
 * envelope and read every counter as 0.
 */
const DASHBOARD_MARKERS = [
  'allusers',
  'activeusers',
  'userloggedindaterange',
  'newusersindaterange',
  'totalpostsindaterange',
  'uniquepostsindaterange',
  'returnusersindaterange',
  'totaluploadphotosindaterange',
  'usersloggedintoday',
  'usersloggedinyesterday',
  'usersloggedinthismonth',
  'usersloggedinpast30days',
];

const carriesDashboardFields = (index: Map<string, unknown>): boolean =>
  DASHBOARD_MARKERS.some(marker => index.has(marker));

const MAX_UNWRAP_DEPTH = 5;

/**
 * Wrapper properties checked before any broader scan, most specific first.
 * GetDashboardNewMetrics actually answers `{ dashboard, status, statusCode }`
 * rather than the flat object the API document shows.
 */
const WRAPPER_KEYS = ['dashboard', 'data', 'result', 'payload'];

/**
 * Locates the dashboard body. The documented shape is flat, the live shape
 * nests it under `dashboard`, and other operations on this controller use a
 * `data` envelope, so the known wrappers are tried in order and anything else
 * is found by shape. Also tolerates a JSON-encoded body and a single-element
 * collection.
 */
const unwrapDashboard = (payload: unknown): Record<string, unknown> | null => {
  const visit = (
    value: unknown,
    depth: number,
  ): Record<string, unknown> | null => {
    if (value == null || depth > MAX_UNWRAP_DEPTH) {
      return null;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        return null;
      }
      try {
        return visit(JSON.parse(trimmed), depth + 1);
      } catch {
        return null;
      }
    }
    if (Array.isArray(value)) {
      for (const entry of value) {
        const found = visit(entry, depth + 1);
        if (found) {
          return found;
        }
      }
      return null;
    }
    if (typeof value !== 'object') {
      return null;
    }

    const record = value as Record<string, unknown>;
    const index = indexByLowerKey(record);
    if (carriesDashboardFields(index)) {
      return record;
    }
    for (const wrapper of WRAPPER_KEYS) {
      const found = visit(index.get(wrapper), depth + 1);
      if (found) {
        return found;
      }
    }
    for (const nested of Object.values(record)) {
      const found = visit(nested, depth + 1);
      if (found) {
        return found;
      }
    }
    return null;
  };

  return visit(payload, 0);
};

const mapDashboardMetric = (entry: unknown): DashboardMetric => {
  const index = indexByLowerKey(
    entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : {},
  );
  return {
    metrickey: optionalText(index.get('metrickey')),
    metricname: optionalText(index.get('metricname')),
    desktop: numeric(index.get('desktop')),
    android: numeric(index.get('android')),
    ios: numeric(index.get('ios')),
  };
};

/**
 * Normalises GetDashboardNewMetrics. Returns null when the payload carries no
 * recognisable dashboard fields so the service can reject rather than render a
 * screen of zeros.
 */
export const mapDashboard = (payload: unknown): DashboardNew | null => {
  const source = unwrapDashboard(payload);
  if (!source) {
    if (IS_DEV) {
      console.warn(
        '[admin] GetDashboardNewMetrics returned an unrecognised shape',
        payload,
      );
    }
    return null;
  }

  const index = indexByLowerKey(source);
  const read = (key: keyof DashboardNew): number =>
    numeric(index.get(key.toLowerCase()));

  const rawMetrics = index.get('metrics');
  const dashboard: DashboardNew = {
    userLoggedInDateRange: read('userLoggedInDateRange'),
    newUsersInDateRange: read('newUsersInDateRange'),
    totalPostsInDateRange: read('totalPostsInDateRange'),
    uniquePostsInDateRange: read('uniquePostsInDateRange'),
    returnUsersInDateRange: read('returnUsersInDateRange'),
    timeOnlineInDateRange: optionalText(index.get('timeonlineindaterange')),
    allUsers: read('allUsers'),
    activeUsers: read('activeUsers'),
    usersloggedIntoday: read('usersloggedIntoday'),
    usersloggedInYesterday: read('usersloggedInYesterday'),
    usersloggedInthisMonth: read('usersloggedInthisMonth'),
    usersLoggedinpast30days: read('usersLoggedinpast30days'),
    totalposts: read('totalposts'),
    uniqueposts: read('uniqueposts'),
    newusers: read('newusers'),
    returnusers: read('returnusers'),
    totalcomments: read('totalcomments'),
    totalshares: read('totalshares'),
    totalUploadPhotosInDateRange: read('totalUploadPhotosInDateRange'),
    metrics: Array.isArray(rawMetrics) ? rawMetrics.map(mapDashboardMetric) : [],
  };

  if (IS_DEV) {
    const mapped = new Set(Object.keys(dashboard).map(key => key.toLowerCase()));
    const unmapped = [...index.keys()].filter(key => !mapped.has(key));
    if (unmapped.length > 0) {
      console.warn(
        '[admin] GetDashboardNewMetrics returned unmapped fields',
        unmapped,
        payload,
      );
    }
  }

  return dashboard;
};

const mapLoginEvent = (item: LoginInfoItem): AdminLoginEvent => ({
  loginAt: text(item.loginTimestamp),
  loggedOutAt: text(item.loggedOutTimestamp),
  ipAddress: text(item.ipAddress),
  deviceId: text(item.deviceId),
  userAgent: text(item.userAgent),
  minutes: count(item.mins),
  result: text(item.result),
});

export const mapAdminUser = (item: AdminUserDetailsResponse): AdminUser => ({
  userId: count(item.userId),
  userGuid: text(item.userGUID),
  userName: text(item.userName),
  email: text(item.email),
  firstName: text(item.firstName),
  lastName: text(item.lastName),
  role: (item.role as AdminUserRole | undefined) ?? null,
  isEmailVerified: item.isEmailVerified === true,
  isUserBlocked: item.isUserBlocked === true,
  isActive: item.isActive === true,
  gender: text(item.gender),
  aboutMe: text(item.aboutMe),
  dateOfBirth: text(item.dob),
  // Distinguished from 0: an account with no date of birth has no age, and a
  // literal zero would read as a newborn.
  age: typeof item.age === 'number' && Number.isFinite(item.age) ? item.age : null,
  country: text(item.country),
  picture: text(item.picture),
  joiningDate: text(item.joiningDate),
  lastLoginOn: text(item.lastLoginOn),
  lastLoginIp: text(item.lastloginIp),
  updatedOn: text(item.updatedOn),
  firstPostDate: text(item.firstpostdate),
  lastPostDate: text(item.lastpostdate),
  totalPosts: count(item.totalpost),
  totalVisits: count(item.totalvisits),
  hasReadOnlyAccess: item.hasReadOnlyAccess === true,
  loginHistory: Array.isArray(item.loginInfo)
    ? item.loginInfo.map(mapLoginEvent)
    : [],
});

/**
 * True when a payload looks like a paged envelope, even when its page is
 * empty.
 *
 * Every collection on these endpoints is `nullable` in the API document, and an
 * unassigned .NET `List<T>` serialises as `null`, so the array being absent
 * cannot be treated as failure — that would turn "no rows match this filter"
 * into an error screen. Key presence is what separates a real empty page from
 * an unusable body.
 */
export const isPagedEnvelope = (
  payload: unknown,
  collectionKey: string,
): boolean => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const index = indexByLowerKey(payload as Record<string, unknown>);
  return (
    index.has(collectionKey.toLowerCase()) ||
    index.has('totalcount') ||
    index.has('totalrecords')
  );
};

/**
 * Normalises any of the paged envelopes the admin endpoints return. Page
 * numbers fall back to the request's own values, because an empty last page
 * comes back with zeroed metadata on some endpoints.
 */
export const mapAdminPage = <TApi, TModel>(
  envelope: AdminPagedResult<TApi> | null | undefined,
  mapItem: (item: TApi) => TModel,
  requested: { pageNumber: number; pageSize: number },
): AdminPage<TModel> => {
  const items = Array.isArray(envelope?.items) ? envelope!.items! : [];
  const pageSize = count(envelope?.pageSize) || requested.pageSize;
  const totalCount = count(envelope?.totalCount);
  const totalPages =
    count(envelope?.totalPages) ||
    (pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0);
  const pageNumber = count(envelope?.pageNumber) || requested.pageNumber;

  return {
    items: items.map(mapItem),
    totalCount,
    pageNumber,
    pageSize,
    totalPages,
    hasNextPage: envelope?.hasNextPage ?? pageNumber < totalPages,
    hasPreviousPage: envelope?.hasPreviousPage ?? pageNumber > 1,
  };
};

const mediaList = (paths: readonly (string | null | undefined)[]): string[] =>
  paths
    .map(path => getFullImageUrl(path))
    .filter((url): url is string => typeof url === 'string');

const readReportedContentData = (
  record: Record<string, unknown>,
): ApiReportedContentData | null => {
  const index = indexByLowerKey(record);
  const posts = index.get('posts');
  const totalRecords = index.get('totalrecords');
  const comments = index.get('comments');

  // The rows themselves, not a count, are what makes a body usable: a payload
  // carrying `totalRecords: 5` and no `posts` would render an empty queue that
  // still believes five reports are waiting, and "Load more" would keep asking
  // for a page that never arrives.
  if (!Array.isArray(posts)) {
    return null;
  }
  return {
    totalRecords: typeof totalRecords === 'number' ? totalRecords : null,
    posts: posts as ApiReportedContentItem[],
    comments: Array.isArray(comments)
      ? (comments as ApiReportedContentComment[])
      : [],
  };
};

/**
 * Unwraps the GetReportedPosts body, which arrives as
 * `{ status, statusCode, data: { totalRecords, posts, comments } }`.
 *
 * Returns null — not an empty page — when nothing recognisable is present, so
 * the service can reject. An empty page here would be indistinguishable from
 * "no reports", which would show operators an empty moderation queue and leave
 * reported content unreviewed.
 */
export const extractReportedContentData = (
  payload: unknown,
): ApiReportedContentData | null => {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const direct = readReportedContentData(record);
    if (direct) {
      return direct;
    }
    const nested = indexByLowerKey(record).get('data');
    if (nested && typeof nested === 'object') {
      const unwrapped = readReportedContentData(nested as Record<string, unknown>);
      if (unwrapped) {
        return unwrapped;
      }
    }
  }
  if (IS_DEV && payload != null) {
    console.warn(
      '[admin] GetReportedPosts returned an unrecognised shape',
      payload,
    );
  }
  return null;
};

export const mapReportedContentItem = (
  item: ApiReportedContentItem,
  commentsById?: ReadonlyMap<number, ApiReportedContentComment>,
): ReportedContentItem => {
  const commentId = count(item.reportedCommentId);
  const isComment = commentId > 0;
  // A reported comment's body lives in the sibling `comments` collection; it is
  // absent when the comment was deleted or falls outside the rows the endpoint
  // returns. The post's own body must not stand in for it — that would put the
  // wrong text, author and media in front of the decision — so the card shows
  // its "no content" state instead.
  const comment = isComment ? commentsById?.get(commentId) : undefined;
  const postTitle = text(item.title);

  return {
    reportedPostId: count(item.reportedPostId),
    postId: count(item.shareId),
    commentId,
    isComment,
    content: isComment ? text(comment?.commentMessage) : text(item.postDescription),
    title: isComment ? '' : postTitle,
    parentPostTitle: isComment ? postTitle : '',
    postImages: isComment
      ? mediaList([comment?.images])
      : mediaList(item.photoAlbum ?? []),
    reason: text(item.reportedComments),
    reportedBy: text(item.reporter?.userName),
    reportedByAvatar: resolveProfilePictureUrl(item.reporter?.userPhoto) ?? '',
    reportedByEmail: '',
    postedBy: text(isComment ? comment?.userName : item.author?.userName),
    postedByAvatar:
      resolveProfilePictureUrl(
        isComment ? comment?.userPhoto : item.author?.userPhoto,
      ) ?? '',
    postedByEmail: '',
    postedOn: text(
      isComment
        ? comment?.createdDateTime
        : item.postDate ?? item.createdDateTime,
    ),
    reportedOn: text(item.reportedTime ?? item.reporter?.reportedTime),
    groupName: '',
    isBlog: text(item.type).toLowerCase() === 'blog',
    likes: count(isComment ? comment?.likeCount : item.likeCount),
    comments: isComment ? 0 : count(item.commentCount),
    shares: 0,
    isReviewed: item.isReportReviewed === true,
    isApproved: item.isReportApproved === true,
    isPostActive: item.isPostActive !== false,
    reviewerComments: text(item.reviewerComments),
  };
};

/* ─── Automated content moderation ──────────────────────────────────────────
   The screening controller documents a flat 200 body, while the rest of the
   admin surface answers `{ status, statusCode, data }`. Both are accepted: the
   envelope is unwrapped when present and fields are read case-insensitively,
   the same way the dashboard body is. */

/** The payload's own body, with a `{ data: … }` envelope peeled off. */
const moderationBody = (payload: unknown): Map<string, unknown> | null => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }
  const index = indexByLowerKey(payload as Record<string, unknown>);
  const nested = index.get('data');
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return indexByLowerKey(nested as Record<string, unknown>);
  }
  return index;
};

const rows = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value.filter(
        (entry): entry is Record<string, unknown> =>
          !!entry && typeof entry === 'object' && !Array.isArray(entry),
      )
    : [];

/** A score or threshold: absent means "not scored", which is not the same as 0. */
const ratio = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const identifier = (value: unknown): number | null => {
  const parsed = numeric(value);
  return parsed > 0 ? parsed : null;
};

const flag = (value: unknown): boolean => value === true;

const mapModerationSignal = (
  record: Record<string, unknown>,
): ModerationSignal => {
  const index = indexByLowerKey(record);
  return {
    categoryId: identifier(index.get('categoryid')),
    categoryKey: text(index.get('categorykey') as string | null | undefined),
    category: readModerationCategory(
      index.get('categorykey') ?? index.get('category'),
    ),
    guidelineTitle: text(
      index.get('guidelinetitle') as string | null | undefined,
    ),
    severity: readModerationSeverity(index.get('severityid')),
    score: ratio(index.get('score')),
    thresholdApplied: ratio(index.get('thresholdapplied')),
    matchedOnText: flag(index.get('matchedontext')),
    matchedOnImage: flag(index.get('matchedonimage')),
    evidenceSnippet: text(
      index.get('evidencesnippet') as string | null | undefined,
    ),
  };
};

export const mapModerationQueueItem = (
  record: Record<string, unknown>,
): ModerationQueueItem => {
  const index = indexByLowerKey(record);
  const rawTargetType = index.get('targettype');
  const targetType = readModerationTargetType(rawTargetType);
  const targetId = numeric(index.get('targetid'));

  return {
    key: `${targetType ?? text(index.get('targettypename') as string | null)}|${targetId}`,
    targetType,
    targetTypeName: text(index.get('targettypename') as string | null | undefined),
    targetId,
    isMediaTarget: isModerationMediaTarget(rawTargetType),
    rulingTargetsParent: hasModerationParentTarget(rawTargetType),
    parentId: identifier(index.get('parentid')),
    surface: readModerationSurface(index.get('surface')),
    moderationStatus: readModerationStatus(index.get('moderationstatus')),
    title: text(index.get('title') as string | null | undefined),
    bodyText: text(index.get('bodytext') as string | null | undefined),
    contentTypeName: text(index.get('contenttypename') as string | null | undefined),
    createdAt: text(index.get('createdat') as string | null | undefined),
    authorUserId: identifier(index.get('authoruserid')),
    authorUserGuid: text(index.get('authoruserguid') as string | null | undefined),
    authorUserName: text(index.get('authorusername') as string | null | undefined),
    authorFirstName: text(index.get('authorfirstname') as string | null | undefined),
    authorPhoto:
      resolveProfilePictureUrl(index.get('authorphoto') as string | null) ?? '',
    isAuthorActive: index.get('isauthoractive') !== false,
    resultId: identifier(index.get('resultid')),
    stage: readModerationStage(index.get('stage')),
    provider: text(index.get('provider') as string | null | undefined),
    providerModel: text(index.get('providermodel') as string | null | undefined),
    severity: readModerationSeverity(index.get('severity')),
    topCategory: readModerationCategory(
      index.get('topcategory') ?? index.get('topcategorykey'),
    ),
    topCategoryKey: text(index.get('topcategorykey') as string | null | undefined),
    topCategoryTitle: text(
      index.get('topcategorytitle') as string | null | undefined,
    ),
    topScore: ratio(index.get('topscore')),
    policyRationale: text(index.get('policyrationale') as string | null | undefined),
    screenedAt: text(index.get('screenedat') as string | null | undefined),
    mediaUrls: mediaList(
      (Array.isArray(index.get('mediaurls'))
        ? (index.get('mediaurls') as unknown[])
        : []
      ).map(entry => (typeof entry === 'string' ? entry : null)),
    ),
    signals: rows(index.get('matchedcategories')).map(mapModerationSignal),
  };
};

/**
 * `null` only when the body has no `items` field at all, which means the shape
 * is not the queue's. A present-but-null collection is the API's way of saying
 * "no rows" — SQL that selected nothing serialises that way — so it maps to an
 * empty page and the screen shows its empty state instead of an error.
 */
export const mapModerationQueue = (
  payload: unknown,
  requested: { startIndex: number; pageSize: number },
): ModerationQueuePage | null => {
  const body = moderationBody(payload);
  if (!body || !body.has('items')) {
    if (IS_DEV && payload != null) {
      console.warn(
        '[admin] GetReviewQueue returned an unrecognised shape',
        payload,
      );
    }
    return null;
  }
  return {
    items: rows(body.get('items')).map(mapModerationQueueItem),
    totalRecords: numeric((body.get('totalrecords'))),
    startIndex: numeric((body.get('startindex'))) || requested.startIndex,
    pageSize: numeric((body.get('pagesize'))) || requested.pageSize,
  };
};

export const mapModerationSetting = (
  record: Record<string, unknown>,
): ModerationSetting => {
  const index = indexByLowerKey(record);
  return {
    settingKey: text(index.get('settingkey') as string | null | undefined),
    settingValue: text(index.get('settingvalue') as string | null | undefined),
    description: text(index.get('description') as string | null | undefined),
    isActive: flag(index.get('isactive')),
    updatedBy: text(index.get('updatedby') as string | null | undefined),
    updatedAtUtc: text(index.get('updatedatutc') as string | null | undefined),
  };
};

/**
 * The single override `ToggleSetting` echoes back. `null` when the body carried
 * no key: the switch still flipped (the call was accepted), so the caller
 * re-reads the settings rather than treating this as a failure.
 */
export const mapToggledModerationSetting = (
  payload: unknown,
): ModerationSetting | null => {
  const body = moderationBody(payload);
  if (!body || !body.has('settingkey')) {
    return null;
  }
  return mapModerationSetting(
    Object.fromEntries(body) as Record<string, unknown>,
  );
};

const mapModerationBacklogCount = (
  record: Record<string, unknown>,
): ModerationBacklogCount => {
  const index = indexByLowerKey(record);
  return {
    surface: readModerationSurface(index.get('surface')),
    moderationStatus: readModerationStatus(index.get('moderationstatus')),
    itemCount: numeric((index.get('itemcount'))),
  };
};

/**
 * `null` when neither field is present. Either one may arrive null — an
 * environment with no overrides and nothing held answers that way — and both
 * then map to empty lists rather than to a failure.
 */
export const mapModerationSettings = (
  payload: unknown,
): ModerationSettingsOverview | null => {
  const body = moderationBody(payload);
  if (!body || (!body.has('settings') && !body.has('backlog'))) {
    if (IS_DEV && payload != null) {
      console.warn('[admin] GetSettings returned an unrecognised shape', payload);
    }
    return null;
  }
  return {
    settings: rows(body.get('settings')).map(mapModerationSetting),
    backlog: rows(body.get('backlog')).map(mapModerationBacklogCount),
  };
};

const mapModerationUsageBreakdown = (
  record: Record<string, unknown>,
  position: number,
): ModerationUsageBreakdown => {
  const index = indexByLowerKey(record);
  const surface = readModerationSurface(index.get('surface'));
  const stage = readModerationStage(index.get('stage'));
  const provider = text(index.get('provider') as string | null | undefined);
  const providerModel = text(
    index.get('providermodel') as string | null | undefined,
  );
  return {
    // The grouping fields are all optional, so position is what guarantees the
    // key is unique; the rest is kept to keep it readable while debugging.
    key: `${position}|${surface ?? ''}|${stage ?? ''}|${provider}|${providerModel}`,
    surface,
    stage,
    provider,
    providerModel,
    callCount: numeric((index.get('callcount'))),
    errorCount: numeric((index.get('errorcount'))),
    promptTokens: numeric((index.get('prompttokens'))),
    cachedPromptTokens: numeric((index.get('cachedprompttokens'))),
    completionTokens: numeric((index.get('completiontokens'))),
    totalCostUsd: numeric((index.get('totalcostusd'))),
  };
};

/**
 * `null` only when the body carries no spend fields at all. A month with no
 * calls yet is a valid zeroed answer, so `totalCostUsd: 0` must map rather
 * than fail.
 */
export const mapModerationUsage = (payload: unknown): ModerationUsage | null => {
  const body = moderationBody(payload);
  const usable =
    body &&
    ['totalcostusd', 'callcount', 'periodstart', 'breakdown'].some(key =>
      body.has(key),
    );
  if (!body || !usable) {
    if (IS_DEV && payload != null) {
      console.warn('[admin] GetUsage returned an unrecognised shape', payload);
    }
    return null;
  }
  return {
    periodStart: text(body.get('periodstart') as string | null | undefined),
    periodEnd: text(body.get('periodend') as string | null | undefined),
    totalCostUsd: numeric((body.get('totalcostusd'))),
    callCount: numeric((body.get('callcount'))),
    errorCount: numeric((body.get('errorcount'))),
    promptTokens: numeric((body.get('prompttokens'))),
    cachedPromptTokens: numeric((body.get('cachedprompttokens'))),
    completionTokens: numeric((body.get('completiontokens'))),
    breakdown: rows(body.get('breakdown')).map(mapModerationUsageBreakdown),
  };
};

/**
 * The only sentence the API offers for "this was already decided" is the
 * message itself, so it is matched here rather than in a screen: two admins on
 * one queue hit it routinely and it must not read as a failure.
 */
const ALREADY_DECIDED = /already/i;

export const mapModerationReviewOutcome = (
  payload: unknown,
): ModerationReviewOutcome | null => {
  const body = moderationBody(payload);
  if (!body || !body.has('issuccess')) {
    if (IS_DEV && payload != null) {
      console.warn('[admin] ApplyReview returned an unrecognised shape', payload);
    }
    return null;
  }
  const message = text(body.get('message') as string | null | undefined);
  return {
    isSuccess: body.get('issuccess') !== false,
    newStatus: readModerationStatus(body.get('newstatus')),
    alreadyInState: ALREADY_DECIDED.test(message),
    message,
  };
};

export const mapReportedContent = (
  data: ApiReportedContentData,
): ReportedContentPage => {
  const commentsById = new Map<number, ApiReportedContentComment>();
  for (const comment of data.comments ?? []) {
    const id = count(comment.commentId);
    if (id > 0 && !commentsById.has(id)) {
      commentsById.set(id, comment);
    }
  }
  return {
    items: (data.posts ?? []).map(post =>
      mapReportedContentItem(post, commentsById),
    ),
    totalRecords: count(data.totalRecords),
  };
};
